import {useCallback, useRef, useEffect} from 'react';

/**
 * Props for the useApiStream hook.
 * @template TResponse The expected type of the data in each SSE message.
 *
 * @author Moeed Ahmad <moeed@250mils.com>
 */
interface UseApiStreamProps<TResponse> {
  onMessage: (data: TResponse) => void;
  onClose?: () => void;
  onError?: (error: unknown) => void;
  /**
   * An optional function to parse the raw string data from the event.
   * Defaults to JSON.parse.
   */
  parser?: (eventData: string) => TResponse;
}

interface UseApiStream<P> {
  connect: (endpoint: string, params: P) => Promise<void>;
  disconnect: VoidFunction;
}

/**
 * A generic React hook for handling Server-Sent Events (SSE) streams
 * initiated via a POST request using the Fetch API.
 * @template TResponse The expected type of the data in each SSE message.
 */
export const useApiStream = <TResponse, P>({onMessage, onClose, onError, parser = JSON.parse}: UseApiStreamProps<TResponse>): UseApiStream<P> => {
  const abortControllerRef = useRef<AbortController | null>(null);

  const connect = useCallback(
    async (endpoint: string, params: P) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const response = await fetch(window.PMW.util.site_url(endpoint), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
          },
          body: JSON.stringify(params),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (!response.body) {
          throw new Error('Response body is null');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const {done, value} = await reader.read();
          if (done) {
            onClose?.();
            break;
          }

          buffer += decoder.decode(value, {stream: true});

          let lastCut = 0;
          for (let i = 0; i < buffer.length; i++) {
            if (buffer[i] === '}') {
              const potentialJson = buffer.substring(lastCut, i + 1);
              if (potentialJson.startsWith('{')) {
                try {
                  const parsedData = parser(potentialJson);
                  onMessage(parsedData);
                  lastCut = i + 1;
                } catch (e: unknown) {
                  // CodeReviewMoeedLater: Consider handling partial JSON objects that may span multiple chunks
                  // Right now this is a logical part of parsing.
                }
              }
            }
          }

          if (lastCut > 0) {
            buffer = buffer.substring(lastCut);
          }
        }
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          console.log('Stream connection aborted by client.');
        } else {
          console.error('SSE (fetch) Error:', error);
          onError?.(error);
        }
      } finally {
        abortControllerRef.current = null;
      }
    },
    [onMessage, onClose, onError, parser]
  );

  const disconnect = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      console.log('SSE (fetch) Disconnected by client');
    }
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return (): void => {
      disconnect();
    };
  }, [disconnect]);

  return {connect, disconnect};
};
